import { cloneElement, isValidElement } from 'react';
import { useTranslation } from 'react-i18next';

import { pipelineStepTypeIcon } from '@/console/features/pipelines/hooks/use-pipeline-step-type/use-pipeline-step-type.constants';
import { TPipelineStepTypeLabel } from '@/console/features/pipelines/hooks/use-pipeline-step-type/use-pipeline-step-type.types';
import { TBaseIcon } from '@/shared/assets/icons/base-icon/base-icon.types';
import { CircleIcon } from '@/shared/assets/icons/circle-icon';
import { FeatureEnum } from '@/shared/types/types.generated';

export const usePipelineStepType = () => {
  const { t } = useTranslation();

  const label: Partial<TPipelineStepTypeLabel> = {
    [FeatureEnum.Deployment]: t('pipelines:deployment'),
    [FeatureEnum.DeviceFirmwareUpdate]: t('pipelines:update_device_firmware'),
    [FeatureEnum.DevicePasswordUpdate]: t('pipelines:rotate_control_password'),
    [FeatureEnum.DeviceSnapshot]: t('pipelines:device_snapshot'),
    [FeatureEnum.DeviceSnapshotRestore]: t('pipelines:device_snapshot_restore'),
    [FeatureEnum.Ideaas]: t('pipelines:ideaas'),
    [FeatureEnum.LicenseValidate]: t('pipelines:license_validate'),
    [FeatureEnum.ProjectBackup]: t(
      'pipelines:pipeline_steps_enum.project_backup',
    ),
    [FeatureEnum.FtpBackup]: t('pipelines:ftp_backup'),
    [FeatureEnum.ProjectConvert]: t('pipelines:project_convert'),
    [FeatureEnum.ProjectLibraryUpgrade]: t(
      'pipelines:pipeline_steps_enum.project_library_upgrade',
    ),
    [FeatureEnum.ProjectMerge]: t('pipelines:project_merge'),
    [FeatureEnum.ProjectView]: t('pipelines:project_view'),
    [FeatureEnum.ReadVariableListFromDevice]: t(
      'pipelines:read_variable_list_from_device',
    ),
    [FeatureEnum.WriteVariableListToDevice]: t(
      'pipelines:write_variables_list_to_device',
    ),
    [FeatureEnum.SmbBackup]: t('pipelines:smb_backup'),
    [FeatureEnum.SmbRestore]: t('pipelines:restore_smb'),
    [FeatureEnum.FtpRestore]: t('pipelines:restore_ftp'),
  };

  const getStepTypeLabel = (type: FeatureEnum) => {
    return label[type] || t('pipelines:unknown_step');
  };

  const getStepTypeIcon = (type: FeatureEnum, props?: TBaseIcon) => {
    const icon = pipelineStepTypeIcon[type] || <CircleIcon />;

    if (!isValidElement(icon)) {
      console.error('Icon must be a valid React element');
      return null;
    }

    return cloneElement(icon, props);
  };

  return {
    getStepTypeLabel,
    getStepTypeIcon,
  };
};
